import React, { useCallback, useEffect } from 'react';
import c from 'classnames';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import { FormCheckboxes, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';

import { useQuizContext } from '../../../context/QuizContext';
import WebformAssessment from '../Assessment';
import WebformQuiz from '../Quiz';
import type { WebformQuizCheckboxes as Type } from '../types';

import './style.scss';

export interface WebformQuizCheckboxesProps {
  entity: ImmutableEntity<Type>;
}

function validateAnswer(givenAnswers: string[], correctAnswers: string[]) {
  return givenAnswers.some((answer) => correctAnswers.includes(answer)) ? 'correct' : 'incorrect';
}

const WebformQuizCheckboxes: React.FC<WebformQuizCheckboxesProps> = (props) => {
  const {
    entity,
  } = props;

  const { t } = useTranslation();
  const { currentQuizQuestion, updateQuizQuestion, updateCurrentQuizQuestionId } = useQuizContext();

  const component = entity.toJS();

  // Base props.
  const key = component['#webform_key'];
  const title = component['#title'];
  const required = component['#required'] || false;
  const requiredError = component['#required_error'] || '';
  const layoutOptions = component['#options_display'] || '';
  const elementClassNames = component['#attributes']?.class[0] || '';
  const wrapperClassNames = c('WebformQuizCheckboxes', component['#wrapper_attributes']?.class[0], {
    [`WebformQuizCheckboxes--${String(layoutOptions)}`]: layoutOptions,
  });

  // Options props.
  const options = component['#options'];

  // Quiz props.
  const correctAnswers = component['#correct_answers'].split(',').map((item) => item.trim());
  const description = component['#description_question'];
  const descriptionCorrectAnswer = component['#description_correct_answer'];
  const descriptionIncorrectAnswer = component['#description_incorrect_answer'];

  const validation = useCallback((value: string) => {
    let error;
    if (required && value.length === 0) {
      error = requiredError || t(['webform.components.quiz_checkboxes.required', 'webform.required', 'webform_required']);
    }
    return error;
  }, [required, requiredError, t]);

  useEffect(() => {
    updateCurrentQuizQuestionId(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={wrapperClassNames}>
      <MotionConfig transition={{ duration: 0.3 }}>
        <AnimatePresence initial={false} mode="wait">
          {currentQuizQuestion?.currentPage === 'question' ? (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="WebformQuizCheckboxes__header">
                <WebformAssessment.Count />
              </div>

              <FormCheckboxes
                className={elementClassNames}
                fieldName={key}
                required={required}
                title={title}
                options={options}
                validation={validation}
                description={description}
                onChange={(value: string, givenAnswers: string[]) => {
                  updateQuizQuestion({
                    id: key,
                    givenAnswers,
                    isCorrect: validateAnswer(givenAnswers, correctAnswers) === 'correct',
                  });
                }}
              />
            </motion.div>
          ) : (
            <motion.div
              key="description"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <WebformQuiz.Thumb
                className="WebformQuizCheckboxes__thumb"
                direction={currentQuizQuestion?.isCorrect ? 'up' : 'down'}
              />
              <div className="WebformQuizCheckboxes__description">
                <WysiwygField
                  property="schema:text"
                  html={currentQuizQuestion?.isCorrect ?
                    descriptionCorrectAnswer :
                    descriptionIncorrectAnswer}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </MotionConfig>
    </div>
  );
};

export default WebformQuizCheckboxes;
